import classNames from 'classnames';
import {useRouter} from 'next/router';
import React, {ComponentProps, PropsWithChildren, ReactElement, useMemo} from 'react';

import {ContactIcon, WebsiteIcon} from '../../../../resources/Icons';
import URLS from '../../../../urls';
import Col from '../../../common/Col';
import {useFirebaseAuth} from '../../../common/EnvironmentProvider';
import Row from '../../../common/Row';
import {useBreakpoint} from '../../../common/util/useBreakpoint';
import {getInitials} from '../../decision/util';
import {ExtendedSupplier} from '../../marketplace/types';
import Button from '../buttons/Button';
import CircleImage from '../CircleImage';
import Layout from '../Layout';
import Select from '../Select';
import StarRatings from '../StarRatings';
import TabBar from '../TabBar';
import Tags from '../Tags';
import EditYourSupplierBanner from './EditYourSupplierBanner';

const LayoutWithSupplier = ({
  title,
  supplier,
  children,
}: PropsWithChildren<{title: string; supplier?: ExtendedSupplier}>) => {
  if (!supplier) throw new Error('Supplier prop is required for pages using LayoutWithSupplier layout');
  const image = supplier.image;
  const router = useRouter();
  const {isMobile} = useBreakpoint('mobile');
  const isPreview = router.asPath.includes('/preview');
  const userId = useFirebaseAuth()?.currentUser?.uid;

  const tabs = useMemo<ComponentProps<typeof TabBar>['options']>(
    () => [
      {id: 'about', label: 'About', href: URLS.marketPlace.supplier(supplier.slug, isPreview).about},
      {
        id: 'products',
        label: 'Products & Services',
        href: URLS.marketPlace.supplier(supplier.slug, isPreview).products(),
      },
      {id: 'reviews', label: 'Reviews', href: URLS.marketPlace.supplier(supplier.slug, isPreview).reviews},
    ],
    [isPreview, supplier.slug]
  );

  let activeTab = 'about';
  if (/\/services(\/|$)/.test(router.pathname)) activeTab = 'services';
  else if (/\/products(\/|$)/.test(router.pathname)) activeTab = 'products';
  else if (/\/reviews(\/|$)/.test(router.pathname)) activeTab = 'reviews';

  const supplierImage = useMemo(
    () =>
      image ? (
        <CircleImage
          size={isMobile ? 'medium' : 'large'}
          src={image.url}
          alt={image.alt || 'Supplier image'}
          imageFit={supplier.image_fit ? 'contain' : 'cover'}
          className={'shrink-0'}
        />
      ) : (
        <div
          className={classNames(
            'w-[15rem] h-[15rem] mobile:w-[6.5rem] mobile:h-[6.5rem]',
            'shrink-0 mt-sm mobile:mt-0 rounded-full flex items-center justify-center bg-primary-500'
          )}
        >
          <div className={'text-3xl mobile:text-lg text-white font-semibold'}>
            {getInitials(supplier.name ?? 'Unknown')}
          </div>
        </div>
      ),
    [image, isMobile, supplier.image_fit, supplier.name]
  );

  const mobileTabs = useMemo(
    () => (
      <Select
        floating
        buttonContent={tabs.find(t => t.id === activeTab)?.label}
        options={tabs.map(t => ({value: t.id, label: t.label}))}
        onOptionSelected={value => {
          const url = tabs.find(t => t.id === value)?.href;
          if (url) router.push(url);
        }}
        selectedValues={[activeTab]}
      />
    ),
    [activeTab, router, tabs]
  );

  return (
    <Layout title={title} fullHeight={'tabletAndUp'}>
      {userId === supplier.owner_id && (
        <EditYourSupplierBanner
          status={!supplier.published ? 'Draft' : supplier.approved ? 'Published' : 'In review'}
          variant={isPreview ? 'preview' : 'edit'}
        />
      )}
      <Row
        className={classNames(
          'w-full max-w-wider mx-auto mt-lg px-sm justify-center gap-sm min-h-0',
          'mobile:flex-col mobile:mt-xs'
        )}
      >
        {isMobile && mobileTabs}
        <Col className="flex-none w-[33rem] mobile:w-auto">
          {!isMobile && (
            <Button
              className={'flex-none self-start mb-sm'}
              variant={'secondary'}
              accessory={'back'}
              href={URLS.marketPlace.suppliers}
            >
              Back to index
            </Button>
          )}
          <Col className={'pb-sm overflow-y-auto [&>*]:shrink-0 gap-xs'}>
            <Col className={'gap-xs mobile:flex-row mobile:items-center'}>
              {supplierImage}
              <Col className={'gap-2'}>
                <h1 className={'text-3xl mobile:text-2xl font-semibold leading-tight'}>{supplier.name}</h1>
                {supplier.rating && (
                  <StarRatings showAverage rating={supplier.rating.average} count={supplier.rating.count} />
                )}
              </Col>
            </Col>
            <Tags tags={supplier.tags} expanded className={'mt-xs'} />
            {supplier.phone && (
              <Button
                className="w-fit fv-outline-internal"
                href={`tel:${supplier.phone}`}
                variant="link"
                accessory={<ContactIcon />}
              >
                {supplier.phone}
              </Button>
            )}
            {supplier.website && (
              <Button
                className="w-fit fv-outline-internal"
                href={supplier.website}
                variant="link"
                external
                accessory={<WebsiteIcon />}
                target={'_blank'}
                rel={'noopener noreferrer'}
              >
                {supplier.website}
              </Button>
            )}
          </Col>
        </Col>
        <Col className="min-w-0 grow gap-xs min-h-0">
          {!isMobile && <TabBar options={tabs} activeId={activeTab} />}
          {children}
        </Col>
      </Row>
    </Layout>
  );
};

export const getToolLayoutWithSupplier = (page: ReactElement, title: string) => (
  <LayoutWithSupplier title={title} supplier={page.props.supplier}>
    {page}
  </LayoutWithSupplier>
);
